/**
 * Bleinks API client used by the Word add-in. Same shape as the web app's
 * api.ts: every request attaches the Firebase ID token, JSON in/out
 * except for the multipart upload.
 */
import { getAccessToken } from './auth';
import { API_URL } from './constants';
export class ApiError extends Error {
    constructor(status, code, message, details) {
        super(message);
        this.status = status;
        this.code = code;
        this.details = details;
        this.name = 'ApiError';
    }
}
async function request(path, init = {}) {
    const token = await getAccessToken();
    const headers = {
        ...init.headers,
    };
    if (token)
        headers['authorization'] = `Bearer ${token}`;
    if (!(init.body instanceof FormData) && init.body !== undefined) {
        headers['content-type'] = headers['content-type'] ?? 'application/json';
    }
    const res = await fetch(`${API_URL}${path}`, { ...init, headers });
    if (!res.ok) {
        let body = {};
        try {
            body = (await res.json());
        }
        catch {
            /* non-JSON */
        }
        throw new ApiError(res.status, body.code ?? 'unknown', body.message ?? res.statusText, body.details);
    }
    if (res.status === 204)
        return undefined;
    return (await res.json());
}
export const api = {
    async me() {
        return request('/api/me');
    },
    async uploadPdfBuffer(pdf, title) {
        const fd = new FormData();
        fd.append('file', new Blob([pdf], { type: 'application/pdf' }), `${title || 'document'}.pdf`);
        fd.append('title', title);
        return request('/api/documents/upload', { method: 'POST', body: fd });
    },
    listDocuments(status) {
        const q = status ? `?status=${encodeURIComponent(status)}&limit=50` : '?limit=50';
        return request(`/api/documents${q}`);
    },
    getDocument(id) {
        return request(`/api/documents/${id}`);
    },
    addSigners(documentId, signers) {
        return request(`/api/documents/${documentId}/signers`, {
            method: 'POST',
            body: JSON.stringify({ signers }),
        });
    },
    saveFields(documentId, fields) {
        return request(`/api/documents/${documentId}/fields`, {
            method: 'PUT',
            body: JSON.stringify({ fields }),
        });
    },
    autoPlaceFields(documentId, tags) {
        return request(`/api/documents/${documentId}/fields/auto-place`, {
            method: 'POST',
            body: JSON.stringify({ tags }),
        });
    },
    sendDocument(id) {
        return request(`/api/documents/${id}/send`, {
            method: 'POST',
            body: JSON.stringify({}),
        });
    },
    cancelDocument(id) {
        return request(`/api/documents/${id}/cancel`, {
            method: 'POST',
            body: JSON.stringify({}),
        });
    },
    // ---- Saved signatures ----
    listSignatures() {
        return request('/api/signatures');
    },
    saveSignature(name, imageBase64) {
        return request('/api/signatures', {
            method: 'POST',
            body: JSON.stringify({ name, image: imageBase64 }),
        });
    },
    deleteSignature(id) {
        return request(`/api/signatures/${id}`, { method: 'DELETE' });
    },
    getSignatureImage(id) {
        return request(`/api/signatures/${id}/image`);
    },
    setDefaultSignature(id) {
        return request(`/api/signatures/${id}/default`, {
            method: 'PATCH',
            body: JSON.stringify({}),
        });
    },
};
